import { Component, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';
import { map, take, catchError } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';

import { ConfigurationService } from '../../configuration.service';
import { ProjectLanguage } from '../../firestore-models/project-language';
import { Exhibit } from '../../firestore-models/exhibit';
import { ApiExhibit } from '../../api-client-module';
import { ExhibitAttribute } from '../../firestore-models/exhibit-attribute';
import { TranslateService } from '@ngx-translate/core';

import { AppConst } from '../../app-const';

@Component({
  selector: 'app-select-exhibits-dialog',
  templateUrl: './select-exhibits-dialog.component.html',
  styleUrls: ['./select-exhibits-dialog.component.css'],
  standalone: false,
})
export class SelectExhibitsDialogComponent implements OnInit, OnDestroy {

  projectLanguages!: Array<ProjectLanguage>;

  private exhibitsCollection!: AngularFirestoreCollection<Exhibit>;
  exhibitsObservable!: Observable<Exhibit[]>;
  exhibitsSubscribe!: Subscription;
  private exhibitAttributesCollection!: AngularFirestoreCollection<ExhibitAttribute>;
  exhibitAttributesObservable!: Observable<ExhibitAttribute[]>;
  exhibitAttributesSubscribe!: Subscription;
  projectId: string;

  displayedColumns: string[] = ['position', 'select', 'exhibit_id', 'attribute', 'name'];
  dataSource = new MatTableDataSource<Exhibit>();
  selection = new SelectionModel<Exhibit>(true, []);

  protected ATTR_SEARCH_WORD_PREFIX: string = 'exhibitAttributes';
  exhibitAttributes!: Array<ExhibitAttribute>;

  searchFilter: any = {};

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  attrCollectionName = AppConst.FIRESTORE_EXHIBIT_ATTR;

  constructor(
    public dialogRef: MatDialogRef<SelectExhibitsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ApiExhibit,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
      this.projectId = this.configurationService.getProjectId();
    }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setCustomFilter();
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    // 展示物取得
    this.exhibitsCollection = this.angularFirestore.collection<Exhibit>(
      AppConst.FIRESTORE_EXHIBITS, ref => ref.where('project_id', '==', this.projectId).orderBy('exhibit_id', 'asc'));
    this.exhibitsObservable = this.exhibitsCollection.valueChanges({ idField: 'id' });
    this.exhibitsSubscribe = this.exhibitsObservable.subscribe(exhibits => {
      this.dataSource.data = this.addFilterValue(exhibits);
    });
    // 展示属性取得
    this.exhibitAttributesCollection = this.angularFirestore.collection<ExhibitAttribute>(
      this.attrCollectionName, ref => ref.where('project_id', '==', this.projectId).orderBy('attribute_id', 'asc'));
    this.exhibitAttributesObservable = this.exhibitAttributesCollection.valueChanges({ idField: 'id' });
    this.exhibitAttributesSubscribe = this.exhibitAttributesObservable.subscribe(exhibitAttributes => {
      this.exhibitAttributes = exhibitAttributes;
    });
  }

  ngOnDestroy(): void {
    this.exhibitsSubscribe.unsubscribe();
    this.exhibitAttributesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Exhibit): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  selectExhibits() {
    let exhibits: Array<ApiExhibit> = new Array<ApiExhibit>();
    for (const row of this.dataSource.data) {
      if (this.selection.isSelected(row)) {
        const exhibit: ApiExhibit = {
          id: row.id,
          exhibit_id: row.exhibit_id,
          exhibit_attribute_id: row.exhibit_attribute_id,
          exhibit_names: row.exhibit_names,
          exhibit_descs: row.exhibit_descs,
          exhibit_files: row.exhibit_files,
          latitude: row.exhibit_geopoint.latitude,
          longitude: row.exhibit_geopoint.longitude,
          show_marker: row.show_marker,
          youtube_codes: row.youtube_codes,
          use_youtube: row.use_youtube,
          externallink_names: row.externallink_names,
          externallink_urls: row.externallink_urls,
          use_externallink: row.use_externallink,
          not_overlap_guide: row.not_overlap_guide,
          project_id: row.project_id,
          enabled: row.enabled,
        };
        exhibits.push(exhibit);
      }
    }
    this.dialogRef.close(exhibits);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'exhibit_id':
          return compare(a.exhibit_id, b.exhibit_id, isAsc);
        case 'attribute':
          const aAttr = a.exhibit_attribute_id ? a.exhibit_attribute_id : '';
          const bAttr = b.exhibit_attribute_id ? b.exhibit_attribute_id : '';
          return compare(aAttr, bAttr, isAsc);
        case 'name':
          const aName = a.exhibit_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          const bName = b.exhibit_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          return compare(aName, bName, isAsc);
        default:
          return 0;
      }
    });
  }

  setCustomFilter() {
    // NOTE: AND検索
    this.dataSource.filterPredicate = (data: any, filter: any) => {
      let matchCount = 0;
      let keys = Object.keys(filter);
      for (const key of keys) {
        if (!filter[key]) {
          // フィルタに値がない場合は全抽出
          matchCount++;
        } else {
          if (data[key].trim().toLowerCase().indexOf(filter[key]) !== -1) {
            matchCount++;
          }
        }
      }
      return (keys.length === matchCount);
    };
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.exhibit_names) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
      doc['filterValueAttr'] = this.ATTR_SEARCH_WORD_PREFIX + doc.exhibit_attribute_id;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchFilter['filterValue'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

  applyFilterAttr(event: any) {
    this.selection.clear();
    let filterValue = '';
    if (event.value) {
      filterValue = this.ATTR_SEARCH_WORD_PREFIX + event.value;
    }
    this.searchFilter['filterValueAttr'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

}
